import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha, Alert, Box, Button, Checkbox, Chip, CircularProgress, IconButton, InputAdornment, Link, Pagination, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { Clear as ClearIcon, OpenInNew as OpenIcon, Search as SearchIcon } from '@mui/icons-material';
import { apiPost } from 'api/service';
import { getSurfaceSearchEngines, surfaceSearch } from 'api/intel';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';

const GREEN = '#22c55e';
const MONO = 'var(--fuzzscan-ui-font-mono)';
const PAGE_SIZE = 25;
const STATUS = {
  ok: { color: GREEN, label: 'ok' }, no_key: { color: '#94a3b8', label: 'no key' },
  auth_error: { color: '#ef4444', label: 'bad key' }, error: { color: '#f59e0b', label: 'error' }
};
const th = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontWeight: 600, borderBottom: 1, borderColor: 'divider' };

// Each engine family has its own syntax; the query is sent as typed, so templates follow the first selected engine.
const FAMILY = { shodan: 'a', zoomeye: 'a', fofa: 'b', hunterhow: 'b' };
const TEMPLATES = {
  a: [['Everything on my domain', (d) => `hostname:"${d}"`], ['Login pages on my domain', (d) => `hostname:"${d}" title:"login"`], ['Certificates naming my domain', (d) => `ssl:"${d}"`]],
  b: [['Everything on my domain', (d) => `domain="${d}"`], ['Login pages on my domain', (d) => `domain="${d}" && title="login"`], ['Certificates naming my domain', (d) => `cert="${d}"`]]
};

const hostOf = (raw) => {
  const v = String(raw || '').trim();
  try { return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `//${v}`, 'http://x').hostname || v; } catch { return v; }
};
const isIp = (v) => /^\d{1,3}(\.\d{1,3}){3}$/.test(v) || v.includes(':') && !v.includes('.');

// One row per IP (or per host when the engine gave no IP): its names, every open port and who found it.
function groupResults(results) {
  const map = new Map();
  results.forEach((r) => {
    const name = r.host ? hostOf(r.host) : '';
    const key = r.ip || name;
    if (!key) return;
    const g = map.get(key) || { key, ip: r.ip || '', names: new Set(), ports: new Map(), country: '', title: '', product: '', sources: new Set() };
    if (name && name !== r.ip) g.names.add(name);
    if (r.port) g.ports.set(String(r.port), r.title || r.product || '');
    g.country = g.country || r.country || '';
    g.title = g.title || r.title || '';
    g.product = g.product || r.product || '';
    g.sources.add(r.source);
    map.set(key, g);
  });
  return [...map.values()].map((g) => ({ ...g, names: [...g.names], sources: [...g.sources], portList: [...g.ports.keys()].sort((a, b) => a - b) }));
}

export default function SurfaceSearchPage() {
  const { currentTargetId, targets } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)), [targets, currentTargetId]);
  const target = userTarget?.target;
  const domain = target && target.type !== 'mobile' && target.value ? hostOf(target.value) : '';

  const [engines, setEngines] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [ticked, setTicked] = useState([]);
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [snack, setSnack] = useState('');

  useEffect(() => {
    getSurfaceSearchEngines().then((d) => {
      const list = d?.engines || [];
      setEngines(list);
      setSelected(list.filter((e) => e.configured).map((e) => e.id)); // start with the engines that have a key
    }).catch(() => setEngines([]));
  }, []);

  const anyConfigured = engines.some((e) => e.configured);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const family = FAMILY[selected[0]] || 'a';
  const firstLabel = engines.find((e) => e.id === selected[0])?.label || 'Shodan';

  const run = async (q = query) => {
    if (!q.trim()) { setError('Enter a search query.'); return; }
    setLoading(true); setError(''); setData(null); setTicked([]); setPage(1);
    try { setData(await surfaceSearch(q.trim(), selected, 50)); }
    catch (e) { setError(e?.message || 'Search failed.'); }
    finally { setLoading(false); }
  };
  const start = (build) => { const q = build(domain); setQuery(q); run(q); };

  const groups = useMemo(() => groupResults(data?.results || []), [data]);
  const paged = groups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const allPicked = paged.length > 0 && paged.every((g) => ticked.includes(g.key));
  const portTotal = groups.reduce((n, g) => n + g.portList.length, 0);
  const toggleTick = (key) => setTicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const togglePage = () => setTicked((p) => (allPicked ? p.filter((k) => !paged.some((g) => g.key === k)) : [...new Set([...p, ...paged.map((g) => g.key)])]));

  const engineById = (id) => engines.find((e) => e.id === id);

  const addToTarget = async (list) => {
    if (!target?.id || !list.length) return;
    const ips = [...new Set(list.map((g) => g.ip).filter((v) => v && isIp(v)))];
    const names = [...new Set(list.flatMap((g) => g.names).filter((v) => v && !isIp(v)))];
    setAdding(true);
    try {
      let created = 0;
      for (const [type, values] of [['ip', ips], ['subdomain', names]]) {
        if (!values.length) continue;
        // eslint-disable-next-line no-await-in-loop
        const res = await apiPost('/api/assets/bulk_create_manual/', { target: target.id, type, values, label: 'Surface search' });
        created += res.created || 0;
      }
      setSnack(created ? `Added ${created} new item${created === 1 ? '' : 's'} to ${userTarget?.name || target.value}.` : 'Everything was already on the target.');
      setTicked([]);
    } catch (e) {
      setError(e?.message || 'Could not add to the target.');
    } finally {
      setAdding(false);
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const rows = [['ip', 'hostnames', 'ports', 'title', 'product', 'country', 'found_by'],
      ...groups.map((g) => [g.ip, g.names.join(' '), g.portList.join(' '), g.title, g.product, g.country, g.sources.join(' ')])];
    const url = URL.createObjectURL(new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'surface-search.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  const chosen = groups.filter((g) => ticked.includes(g.key));

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Surface Search</Typography>
        <Typography variant="body2" color="text.secondary">Search Shodan, FOFA, ZoomEye and hunter.how at once and add what you find to your target</Typography>
      </Box>

      {!anyConfigured && engines.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No search-engine keys yet. Add Shodan, FOFA, ZoomEye or hunter.how keys in{' '}
          <Link component={RouterLink} to="/api-keys" sx={{ fontWeight: 700 }}>Integrations</Link> to enable live search.
        </Alert>
      )}

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper', mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" gap={1.2} flexWrap="wrap">
          <TextField fullWidth value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder='hostname:"example.com"' sx={{ flex: '1 1 480px', width: 'auto' }}
            InputProps={{
              sx: { fontFamily: MONO, fontSize: 15, height: 52 },
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: GREEN }} /></InputAdornment>,
              endAdornment: query ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear" onClick={() => setQuery('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null
            }} />
          <Button variant="contained" disabled={loading || !selected.length} onClick={() => run()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ height: 52, px: 4, bgcolor: GREEN, color: '#06220F', fontWeight: 700, fontSize: 15, '&:hover': { bgcolor: '#16a34a' } }}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Engines</Typography>
          {engines.map((e) => {
            const on = selected.includes(e.id);
            return (
              <Tooltip key={e.id} title={e.configured ? e.syntax : `Add a ${e.label} key in Integrations to enable`}>
                <Chip clickable onClick={() => toggle(e.id)}
                  icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: e.configured ? GREEN : '#475569', ml: '10px !important' }} />}
                  label={<>{e.label}{!e.configured && <Box component="span" sx={{ ml: 0.8, fontSize: 11, color: 'text.secondary' }}>add a key</Box>}</>}
                  sx={{ height: 36, fontWeight: 600, border: 1, borderColor: on ? alpha(GREEN, 0.5) : 'divider', bgcolor: on ? alpha(GREEN, 0.08) : 'transparent', color: e.configured ? 'text.primary' : 'text.secondary' }} />
              </Tooltip>
            );
          })}
        </Stack>

        {domain && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ borderTop: 1, borderColor: 'divider', pt: 1.8 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Start from</Typography>
            {TEMPLATES[family].map(([label, build]) => (
              <Chip key={label} clickable label={label} disabled={loading || !selected.length} onClick={() => start(build)} variant="outlined" sx={{ borderRadius: 2.5, height: 34 }} />
            ))}
            <Typography variant="caption" color="text.secondary">written for {domain} in {firstLabel} syntax</Typography>
          </Stack>
        )}
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Typography sx={{ fontWeight: 700, fontSize: 16, mr: 0.5 }}>{groups.length} host{groups.length === 1 ? '' : 's'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{portTotal} open ports</Typography>
              {Object.entries(data.engines || {}).map(([id, st]) => {
                const meta = STATUS[st.status] || STATUS.error;
                const eng = engineById(id);
                return (
                  <Tooltip key={id} title={st.detail || ''}>
                    <Chip size="small" label={`${eng?.label || id}: ${meta.label}${st.status === 'ok' ? ` · ${st.results?.length || 0}` : ''}`}
                      sx={{ fontWeight: 700, bgcolor: alpha(meta.color, 0.14), color: meta.color }} />
                  </Tooltip>
                );
              })}
            </Stack>
            <Stack direction="row" gap={1}>
              <Button variant="outlined" disabled={!groups.length} onClick={exportCsv} sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Export CSV</Button>
              <Button variant="contained" disabled={adding || !target?.id || !chosen.length} onClick={() => addToTarget(chosen)}
                sx={{ bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>
                {chosen.length ? `Add ${chosen.length} selected to ${userTarget?.name || target?.value}` : 'Tick hosts to add'}
              </Button>
            </Stack>
          </Stack>

          {groups.length === 0 ? (
            <Alert severity="warning" sx={{ m: 2.5 }}>No hosts returned. Check the query syntax for the selected engines, that their keys are valid, or widen the search.</Alert>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...th, pl: 2, width: 48 }}><Checkbox size="small" checked={allPicked} onChange={togglePage} inputProps={{ 'aria-label': 'Tick all hosts on this page' }} sx={{ '&.Mui-checked': { color: GREEN } }} /></TableCell>
                    <TableCell sx={th}>Host</TableCell><TableCell sx={th}>Ports</TableCell><TableCell sx={th}>Title</TableCell>
                    <TableCell sx={th}>Product</TableCell><TableCell sx={th}>Country</TableCell><TableCell sx={th}>Found by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((g) => (
                    <TableRow key={g.key} hover selected={ticked.includes(g.key)}>
                      <TableCell sx={{ pl: 2 }}><Checkbox size="small" checked={ticked.includes(g.key)} onChange={() => toggleTick(g.key)} inputProps={{ 'aria-label': `Tick ${g.key}` }} sx={{ '&.Mui-checked': { color: GREEN } }} /></TableCell>
                      <TableCell sx={{ py: 1.6, maxWidth: 300 }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{g.names[0] || g.ip}</Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: 'text.secondary' }}>
                          {g.names.length > 1 ? `+${g.names.length - 1} more name${g.names.length === 2 ? '' : 's'} · ` : ''}{g.names[0] ? g.ip : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {g.portList.length ? g.portList.map((p) => (
                            <Tooltip key={p} title={g.ports.get(p) || ''}><Chip size="small" label={p} sx={{ fontFamily: MONO, fontSize: 12, height: 22 }} /></Tooltip>
                          )) : '—'}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, maxWidth: 200 }}><Typography noWrap variant="body2" title={g.title}>{g.title || '—'}</Typography></TableCell>
                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{g.product || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{g.country || '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} alignItems="center">
                          {g.sources.map((s) => {
                            const e = engineById(s);
                            const c = e?.color || GREEN;
                            return <Chip key={s} size="small" label={e?.label || s} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(c, 0.16), color: c }} />;
                          })}
                          {g.names[0] && (
                            <Tooltip title="Open in a new tab"><IconButton size="small" aria-label={`Open ${g.names[0]}`} component="a" href={`http://${g.names[0]}`} target="_blank" rel="noopener noreferrer"><OpenIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {groups.length > PAGE_SIZE && (
            <Stack alignItems="center" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Pagination count={Math.ceil(groups.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} size="small" />
            </Stack>
          )}
        </Box>
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
